import { DOCUMENT, isPlatformBrowser } from '@angular/common';
import {
  ChangeDetectionStrategy,
  Component,
  ElementRef,
  Injector,
  PLATFORM_ID,
  afterNextRender,
  computed,
  effect,
  inject,
  output,
  viewChild,
} from '@angular/core';
import { TranslateModule } from '@ngx-translate/core';
import { BadgeDirective, ButtonDirective } from '@imagise/shared';
import { ANALYTICS_CONSENT_SETTINGS_ID, AnalyticsConsentService } from './analytics-consent.service';

@Component({
  selector: 'lib-analytics-consent',
  imports: [BadgeDirective, ButtonDirective, TranslateModule],
  templateUrl: './analytics-consent.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
  host: {
    // Avoid display:contents — it breaks position:fixed containing blocks in some browsers.
    class: 'block',
    '(document:keydown.escape)': 'closeSettings()',
  },
})
export class AnalyticsConsentComponent {
  readonly acceptConsent = output<void>();
  readonly rejectConsent = output<void>();

  protected readonly consent = inject(AnalyticsConsentService);

  readonly #document = inject(DOCUMENT);
  readonly #injector = inject(Injector);
  readonly #platformId = inject(PLATFORM_ID);
  private readonly panel = viewChild<ElementRef<HTMLElement>>('panel');

  protected readonly showBanner = computed(
    () =>
      this.consent.initialized() &&
      (this.consent.choice() === null || this.consent.settingsOpen())
  );

  protected readonly canClose = computed(
    () => this.consent.settingsOpen() && this.consent.choice() !== null
  );

  protected readonly describedBy = computed(() =>
    this.consent.choice()
      ? 'analytics-consent-description analytics-consent-status'
      : 'analytics-consent-description'
  );

  constructor() {
    effect(() => {
      if (!isPlatformBrowser(this.#platformId) || !this.consent.settingsOpen()) {
        return;
      }

      afterNextRender(
        () => this.panel()?.nativeElement.focus({ preventScroll: true }),
        { injector: this.#injector }
      );
    });
  }

  protected accept(): void {
    this.#submitChoice(() => this.acceptConsent.emit());
  }

  protected reject(): void {
    this.#submitChoice(() => this.rejectConsent.emit());
  }

  protected closeSettings(): void {
    if (!this.consent.settingsOpen()) {
      return;
    }

    this.consent.closeSettings();
    this.#focusSettingsTrigger();
  }

  #submitChoice(emitChoice: () => void): void {
    const returnFocus = this.consent.settingsOpen();
    emitChoice();

    if (returnFocus) {
      this.#focusSettingsTrigger();
    }
  }

  #focusSettingsTrigger(): void {
    afterNextRender(
      () =>
        this.#document.getElementById(ANALYTICS_CONSENT_SETTINGS_ID)?.focus({ preventScroll: true }),
      { injector: this.#injector }
    );
  }
}
